/**
 * Четыре представления задач (docs/07-tasks-notion.md).
 * Данные общие, меняется только способ показа.
 */
import { useMemo, useState } from 'react';
import { Button, Card, Icon } from '@/ds';
import type { TaskRow, TaskStatus, TasksResponse } from '@/api/hooks';
import { formatDateShort, plural } from '@/lib/format';
import { useIsMobile } from '@/lib/useMediaQuery';
import {
  Assignees,
  DueDate,
  NotionLink,
  ProjectTag,
  STATUS_LABELS,
  STATUS_ORDER,
  StatusBadge,
  byDueDate,
  isOverdue,
  todayIso,
} from './shared';

// --------------------------------------------------------------- таблица

type SortKey = 'title' | 'status' | 'dueDate' | 'projectName';

export function TaskTable({ tasks }: { tasks: TaskRow[] }) {
  const isMobile = useIsMobile();
  const [sortKey, setSortKey] = useState<SortKey>('dueDate');
  const [ascending, setAscending] = useState(true);

  const sorted = useMemo(() => {
    const copy = [...tasks];
    copy.sort((a, b) => {
      // Срок сортируется особым образом: задачи без срока всегда в конце.
      if (sortKey === 'dueDate') return byDueDate(a, b);
      const left = String(a[sortKey] ?? '');
      const right = String(b[sortKey] ?? '');
      return left.localeCompare(right, 'ru');
    });
    return ascending ? copy : copy.reverse();
  }, [tasks, sortKey, ascending]);

  function toggle(key: SortKey) {
    if (key === sortKey) setAscending(!ascending);
    else {
      setSortKey(key);
      setAscending(true);
    }
  }

  function Head({ label, sortBy }: { label: string; sortBy: SortKey }) {
    return (
      <th>
        <button
          onClick={() => toggle(sortBy)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
            color: 'inherit',
            letterSpacing: 'inherit',
            textTransform: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {label}
          {sortKey === sortBy ? (
            <Icon name={ascending ? 'ChevronUp' : 'ChevronDown'} size={14} />
          ) : null}
        </button>
      </th>
    );
  }

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {sorted.map((task) => (
          <Card key={task.id} padding="sm">
            <div style={{ font: '500 14px/1.4 var(--font-text)', color: 'var(--text-strong)' }}>
              {task.title}
            </div>
            <div
              style={{
                marginTop: 'var(--space-3)',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-3)',
                alignItems: 'center',
              }}
            >
              <StatusBadge status={task.status} />
              <DueDate task={task} />
              <ProjectTag name={task.projectName} />
            </div>
            <div style={{ marginTop: 'var(--space-3)' }}>
              <Assignees people={task.assignees} />
            </div>
            {task.comment ? (
              <div
                style={{
                  marginTop: 'var(--space-3)',
                  font: '400 13px/1.5 var(--font-text)',
                  color: 'var(--text-muted)',
                }}
              >
                {task.comment}
              </div>
            ) : null}
            <div style={{ marginTop: 'var(--space-4)' }}>
              <NotionLink task={task} />
            </div>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <Card padding="md">
      <div className="kj-scroll-x">
        <table className="kj-table">
          <thead>
            <tr>
              <Head label="Задача" sortBy="title" />
              <th>Ответственные</th>
              <Head label="Статус" sortBy="status" />
              <Head label="Срок" sortBy="dueDate" />
              <Head label="Продукт" sortBy="projectName" />
              <th>Комментарий</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {sorted.map((task) => (
              <tr key={task.id}>
                <td data-label="Задача" style={{ color: 'var(--text-strong)', minWidth: 220 }}>{task.title}</td>
                <td data-label="Ответственные">
                  <Assignees people={task.assignees} />
                </td>
                <td data-label="Статус">
                  <StatusBadge status={task.status} />
                </td>
                <td data-label="Срок">
                  <DueDate task={task} />
                </td>
                <td data-label="Продукт">
                  <ProjectTag name={task.projectName} />
                </td>
                <td data-label="Комментарий" style={{ color: 'var(--text-muted)', maxWidth: 260 }}>{task.comment ?? '—'}</td>
                <td data-label="" style={{ textAlign: 'right' }}>
                  <NotionLink task={task} compact />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------- доска

function BoardCard({ task }: { task: TaskRow }) {
  return (
    <Card padding="sm" elevation="xs">
      <div style={{ font: '500 14px/1.4 var(--font-text)', color: 'var(--text-strong)' }}>
        {task.title}
      </div>
      <div
        style={{
          marginTop: 'var(--space-3)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-3)',
          alignItems: 'center',
        }}
      >
        <DueDate task={task} />
        <ProjectTag name={task.projectName} />
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        <Assignees people={task.assignees} />
      </div>
      <div style={{ marginTop: 'var(--space-3)' }}>
        <NotionLink task={task} compact />
      </div>
    </Card>
  );
}

export function TaskBoard({ tasks }: { tasks: TaskRow[] }) {
  // Колонка Done по умолчанию свёрнута до счётчика.
  const [doneOpen, setDoneOpen] = useState(false);

  const columns = STATUS_ORDER.map((status) => ({
    status,
    items: tasks.filter((task) => task.status === status).sort(byDueDate),
  }));

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 'var(--space-5)',
        alignItems: 'start',
      }}
    >
      {columns.map((column) => {
        const collapsed = column.status === 'done' && !doneOpen;
        return (
          <Card key={column.status} tone="sunken" padding="sm" elevation="none">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
              }}
            >
              <span style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>
                {STATUS_LABELS[column.status]}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-2xs)',
                  color: 'var(--text-subtle)',
                }}
              >
                {column.items.length}
              </span>
            </div>

            {collapsed ? (
              <Button variant="ghost" size="sm" onClick={() => setDoneOpen(true)}>
                Показать выполненные
              </Button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {column.items.length === 0 ? (
                  <span style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
                    Пусто
                  </span>
                ) : (
                  column.items.map((task) => <BoardCard key={task.id} task={task} />)
                )}
                {column.status === 'done' && doneOpen ? (
                  <Button variant="ghost" size="sm" onClick={() => setDoneOpen(false)}>
                    Свернуть
                  </Button>
                ) : null}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------- по людям

export function TaskByPeople({ data }: { data: TasksResponse }) {
  const groups = data.people
    .map((person) => ({
      key: person.notionUserId,
      name: person.name,
      linked: person.userId !== null,
      tasks: data.tasks
        .filter((task) => task.assignees.some((a) => a.notionUserId === person.notionUserId))
        .sort(byDueDate),
    }))
    .filter((group) => group.tasks.length > 0);

  const unassigned = data.tasks.filter((task) => task.assignees.length === 0).sort(byDueDate);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      {groups.length === 0 && unassigned.length === 0 ? (
        <Card padding="md">
          <span style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
            С выбранными фильтрами задач нет.
          </span>
        </Card>
      ) : null}

      {groups.map((group) => {
        const inProgress = group.tasks.filter((task) => task.status === 'in_progress').length;
        return (
          <Card key={group.key} padding="md">
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 'var(--space-4)',
                flexWrap: 'wrap',
              }}
            >
              <span
                style={{
                  font: '500 16px var(--font-display)',
                  letterSpacing: 'var(--ls-heading)',
                  color: 'var(--text-strong)',
                }}
              >
                {group.name}
              </span>
              <span style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
                {inProgress > 0
                  ? `${inProgress} ${plural(inProgress, 'задача', 'задачи', 'задач')} в работе`
                  : 'в работе ничего'}
                {group.linked ? '' : ' · не сопоставлен с пользователем'}
              </span>
            </div>

            <div
              style={{
                marginTop: 'var(--space-5)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
              }}
            >
              {group.tasks.map((task) => (
                <div
                  key={task.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-4)',
                    flexWrap: 'wrap',
                    paddingBottom: 'var(--space-3)',
                    borderBottom: '1px solid var(--border-subtle)',
                  }}
                >
                  <span
                    style={{
                      flex: '1 1 240px',
                      font: '400 14px var(--font-text)',
                      color: 'var(--text-body)',
                      minWidth: 0,
                    }}
                  >
                    {task.title}
                  </span>
                  <StatusBadge status={task.status} />
                  <DueDate task={task} />
                  <NotionLink task={task} compact />
                </div>
              ))}
            </div>
          </Card>
        );
      })}

      {unassigned.length > 0 ? (
        <Card padding="md" tone="sunken" elevation="none">
          <div
            style={{
              font: '500 16px var(--font-display)',
              letterSpacing: 'var(--ls-heading)',
              color: 'var(--text-strong)',
            }}
          >
            Не назначено
          </div>
          <div
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
            }}
          >
            {unassigned.map((task) => (
              <div
                key={task.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-4)',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    flex: '1 1 240px',
                    font: '400 14px var(--font-text)',
                    color: 'var(--text-body)',
                  }}
                >
                  {task.title}
                </span>
                <DueDate task={task} />
                <NotionLink task={task} compact />
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}

// -------------------------------------------------------------- таймлайн

type Scale = 'week' | 'month' | 'quarter';

const SCALE_DAYS: Record<Scale, number> = { week: 7, month: 31, quarter: 92 };

export function TaskTimeline({ data }: { data: TasksResponse }) {
  const [scale, setScale] = useState<Scale>('month');

  const dated = data.tasks.filter((task) => task.dueDate !== null).sort(byDueDate);
  const today = todayIso();

  // Текущая дата примерно по центру окна.
  const span = SCALE_DAYS[scale];
  const todayTime = new Date(`${today}T00:00:00.000Z`).getTime();
  const from = new Date(todayTime - Math.floor(span / 2) * 86_400_000);
  const to = new Date(from.getTime() + span * 86_400_000);

  const position = (iso: string) => {
    const time = new Date(`${iso}T00:00:00.000Z`).getTime();
    return ((time - from.getTime()) / (to.getTime() - from.getTime())) * 100;
  };

  const visible = dated.filter((task) => {
    const start = position(task.dueDate as string);
    const end = position(task.dueDateEnd ?? (task.dueDate as string));
    return end >= 0 && start <= 100;
  });

  const todayLeft = position(today);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ font: '400 13px var(--font-text)', color: 'var(--text-muted)' }}>
          {`${formatDateShort(from)} — ${formatDateShort(to)}`}
        </span>
        <span style={{ display: 'flex', gap: 'var(--space-3)' }}>
          {(['week', 'month', 'quarter'] as Scale[]).map((value) => (
            <Button
              key={value}
              size="sm"
              variant={scale === value ? 'soft' : 'ghost'}
              onClick={() => setScale(value)}
            >
              {value === 'week' ? 'Неделя' : value === 'month' ? 'Месяц' : 'Квартал'}
            </Button>
          ))}
        </span>
      </div>

      <Card padding="md">
        <div className="kj-scroll-x">
          <div style={{ minWidth: 640, position: 'relative' }}>
            {/* Вертикальная линия «сегодня». */}
            <div
              style={{
                position: 'absolute',
                left: `${todayLeft}%`,
                top: 0,
                bottom: 0,
                width: 2,
                background: 'var(--burgundy-700)',
                opacity: 0.5,
              }}
            />

            {visible.length === 0 ? (
              <div
                style={{
                  padding: 'var(--space-8) 0',
                  textAlign: 'center',
                  font: '400 13px var(--font-text)',
                  color: 'var(--text-subtle)',
                }}
              >
                В этом окне задач со сроком нет
              </div>
            ) : (
              visible.map((task) => {
                const start = Math.max(position(task.dueDate as string), 0);
                const end = Math.min(position(task.dueDateEnd ?? (task.dueDate as string)), 100);
                const width = Math.max(end - start, 0);
                const overdue = isOverdue(task);

                return (
                  <div
                    key={task.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-4)',
                      padding: 'var(--space-3) 0',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                  >
                    <a
                      href={task.notionUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="kj-link-plain"
                      style={{
                        flex: '0 0 200px',
                        font: '400 13px var(--font-text)',
                        color: 'var(--text-body)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.title}
                    </a>
                    <span style={{ flex: 1, position: 'relative', height: 20 }}>
                      {task.dueDateEnd ? (
                        <span
                          title={`${task.dueDate} — ${task.dueDateEnd}`}
                          style={{
                            position: 'absolute',
                            left: `${start}%`,
                            width: `${Math.max(width, 1)}%`,
                            top: 6,
                            height: 8,
                            borderRadius: 999,
                            background: overdue ? 'var(--amber-600)' : 'var(--rose-300)',
                          }}
                        />
                      ) : (
                        <span
                          title={task.dueDate ?? ''}
                          style={{
                            position: 'absolute',
                            left: `calc(${start}% - 4px)`,
                            top: 6,
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: overdue ? 'var(--amber-600)' : 'var(--burgundy-700)',
                          }}
                        />
                      )}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </Card>

      <span style={{ font: '400 13px var(--font-text)', color: 'var(--text-subtle)' }}>
        {data.withoutDueDate > 0
          ? `${data.withoutDueDate} ${plural(data.withoutDueDate, 'задача', 'задачи', 'задач')} без срока: на таймлайн они не попадают`
          : 'Все задачи со сроком'}
      </span>
    </div>
  );
}
